import React, { useEffect, useState } from 'react';
import LandingPage from './pages/Landing';
import Content from './pages/Content';
import Snackbar from './components/Snackbar/Snackbar';
import RouteContainer from './components/RouteContainer/RouteContainer';
import { useBUData, useUserRoles } from './hooks';
import Loader from './components/Loader/Loader';
import { useIdleTimer } from 'react-idle-timer';
import { RootState } from './store';
import UserDataSelect from './pages/UserDataSelect';
import { Provider, useSelector } from 'react-redux';
import { BrowserRouter as Router, Route, Switch } from 'react-router-dom';
import { createGlobalStyle as GlobalFonts } from '@cambridgeassessment/cambridge-ui';
import store from './store';
import { Unauthorised } from './pages/Error/401/Unauthorised';
import { InternalError } from './pages/Error/500/InternalError';
import AuthRedirect from './components/AuthRedirect/AuthRedirect';
import './index.css';
import { Redirect, useHistory } from 'react-router';
import { AuthProvider, useAuth } from 'oidc-react';
import { startPinger } from 'lib-browser';
import DowntimePage from './pages/DowntimeScreen/DowntimePage';
import { FlagProvider, useUnleashClient } from '@unleash/proxy-client-react';
import { featureFlagsConfig } from './config';
import { INTERNAL_ROLES } from '@cambridgeassessment/lib-ee-commons';

const App: React.FC<object> = () => {
  const { userData, isLoading } = useAuth();
  const { businessUnit } = useSelector((state: RootState) => state.userData);
  const [initialPingCompleted, setInitialPingCompleted] = useState(false);
  const history = useHistory();
  const unleashContext = useUnleashClient();

  useIdleTimer({
    timeout: 1000 * Number(window.env.IDLE_TIMEOUT_SEC),
    onIdle: () => (window.location.href = `${window.env.SAP_CDC_LOGOUT}?redirect_uri=${window.location.origin}`),
  });

  const userRoles = useUserRoles();

  let validUser = false;
  const logoutToError = (number: number): void => {
    validUser = false;
    window.location.href = `${window.location.origin}/${number}`;
  };

  useEffect(() => {
    if (userData && !initialPingCompleted) {
      const onRedirect = () => history.replace('/maintenance');

      startPinger({ onRedirect }).finally(() => setInitialPingCompleted(true));
    }
  }, [userData]);

  useEffect(() => {
    if (userData) unleashContext.updateContext({ userId: userData.profile.email });
  }, [userData, unleashContext]);

  if (!userData && !isLoading) {
    logoutToError(500);
  } else if (!userRoles.some((value) => INTERNAL_ROLES.includes(value.name)) && !isLoading) {
    logoutToError(401);
  } else if (userData && userRoles) {
    validUser = true;
  }

  useBUData();

  return (
    <>
      <Snackbar />
      {validUser && initialPingCompleted ? (
        <RouteContainer>
          {businessUnit ? (
            <Switch>
              <Route exact path='/' component={LandingPage} />
              <Route path='/' component={Content} />
              <Route path='*' component={(): React.ReactElement => <Redirect to='/' />} />
            </Switch>
          ) : (
            <UserDataSelect />
          )}
        </RouteContainer>
      ) : (
        <Route path='*' component={Loader} />
      )}
    </>
  );
};

const Root: React.FC = () => {
  if (window.env.UNDER_MAITENANCE === 'true') {
    return (
      <Router>
        <GlobalFonts />
        <Switch>
          <Route path='*' component={DowntimePage} />
        </Switch>
      </Router>
    );
  }

  return (
    <FlagProvider config={featureFlagsConfig}>
      <Provider store={store}>
        <AuthProvider
          authority={window.env.SAP_CDC_DOMAIN}
          clientId={window.env.SAP_CDC_CLIENT_ID}
          redirectUri={`${window.location.origin}/callback`}
          scope='openid profile email roles orgs systemIDs claims businessStream'
          automaticSilentRenew={true}
        >
          <Router>
            <GlobalFonts />
            <Switch>
              <Route exact path='/maintenance' component={DowntimePage} />
              <Route exact path='/callback/' render={(props): React.ReactElement => <AuthRedirect {...props} />} />
              <Route exact path='/401' component={Unauthorised} />
              <Route exact path='/500' component={InternalError} />
              <Route path='*' render={(props) => <App {...props} />} />
            </Switch>
          </Router>
        </AuthProvider>
      </Provider>
    </FlagProvider>
  );
};
export default Root;
